import React, { createContext, useContext, useEffect, useState } from "react";
import {
  interestsService,
  type FavoriteRecord,
  type VisitorRecord,
} from "@/services";
import { useUser } from "./UserContext";

interface InterestsContextType {
  favoritesGiven: FavoriteRecord[];
  favoritesReceived: FavoriteRecord[];
  visitors: VisitorRecord[];
  interestsLoading: boolean;
}

const InterestsContext = createContext<InterestsContextType>({
  favoritesGiven: [],
  favoritesReceived: [],
  visitors: [],
  interestsLoading: false,
});

export const InterestsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useUser();
  const [favoritesGiven, setFavoritesGiven] = useState<FavoriteRecord[]>([]);
  const [favoritesReceived, setFavoritesReceived] = useState<FavoriteRecord[]>(
    [],
  );
  const [visitors, setVisitors] = useState<VisitorRecord[]>([]);
  const [interestsLoading, setInterestsLoading] = useState(false);

  useEffect(() => {
    if (!user?.uid) {
      setFavoritesGiven([]);
      setFavoritesReceived([]);
      setVisitors([]);
      setInterestsLoading(false);
      return;
    }

    setInterestsLoading(true);

    const loaded = {
      given: false,
      received: false,
      visitors: false,
    };

    const updateLoading = () => {
      setInterestsLoading(
        !(loaded.given && loaded.received && loaded.visitors),
      );
    };

    const unsubGiven = interestsService.subscribeFavoritesGiven(
      user.uid,
      (data) => {
        setFavoritesGiven(data);
        loaded.given = true;
        updateLoading();
      },
      () => {
        loaded.given = true;
        updateLoading();
      },
    );

    const unsubReceived = interestsService.subscribeFavoritesReceived(
      user.uid,
      (data) => {
        setFavoritesReceived(data);
        loaded.received = true;
        updateLoading();
      },
      () => {
        loaded.received = true;
        updateLoading();
      },
    );

    const unsubVisitors = interestsService.subscribeVisitors(
      user.uid,
      (data) => {
        setVisitors(data);
        loaded.visitors = true;
        updateLoading();
      },
      () => {
        loaded.visitors = true;
        updateLoading();
      },
    );

    return () => {
      unsubGiven();
      unsubReceived();
      unsubVisitors();
    };
  }, [user?.uid]);

  return (
    <InterestsContext.Provider
      value={{ favoritesGiven, favoritesReceived, visitors, interestsLoading }}
    >
      {children}
    </InterestsContext.Provider>
  );
};

export const useInterests = () => useContext(InterestsContext);
